import { create } from 'zustand';
import { Storage, StorageUpdateData, NotificationItem, RecentService } from '../Types/Storage';
import API from '../api2';

export interface StorageHealthData {
  healthIndex: number | null;
  capacity: number | null;
  iotData?: any; // Store iotData to access alarms and other IoT information
}

interface StorageStore {
  storages: Storage[];
  selectedStorageId: string | null;
  notifications: NotificationItem[];
  recentServicesByStorage: Record<string, RecentService[]>; // Changed from recentServices array to per-storage map
  healthDataByStorage: Record<string, StorageHealthData>; // Track health and capacity data
  loading: boolean;
  notificationsLoading: boolean;
  servicesLoading: boolean;
  error: string | null;

  // Actions
  setStorages: (storages: Storage[]) => void;
  setSelectedStorage: (storageId: string) => void;
  updateStorageData: (storageId: string, data: StorageUpdateData) => void;
  addNotification: (notification: NotificationItem) => void;
  dismissNotification: (notificationId: string) => void;
  loadNotifications: (customerId: string) => Promise<void>;
  setRecentServices: (storageId: string, services: RecentService[]) => void;
  getRecentServices: (storageId: string) => RecentService[]; // New getter for specific storage
  setStorageHealthData: (storageId: string, healthData: StorageHealthData) => void;
  getStorageHealthData: (storageId: string) => StorageHealthData | null;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // Computed getters
  getSelectedStorage: () => Storage | null;
}

export const useStorageStore = create<StorageStore>((set, get) => ({
  storages: [],
  selectedStorageId: null,
  notifications: [],
  recentServicesByStorage: {}, // Initialize as empty object
  healthDataByStorage: {}, // Initialize as empty object
  loading: false,
  notificationsLoading: false,
  servicesLoading: false,
  error: null,

  setStorages: (storages) => set({ storages }),

  setSelectedStorage: (storageId) => set({ selectedStorageId: storageId }),

  updateStorageData: (storageId, data) => set((state) => ({
    storages: state.storages.map(storage =>
      storage.id === storageId
        ? { ...storage, ...data, lastUpdated: new Date() }
        : storage
    )
  })),

  addNotification: (notification) => set((state) => ({
    notifications: [notification, ...state.notifications]
  })),

  dismissNotification: async (notificationId: string) => {
    try {
      // First check if it's a lab notification (needs API call)
      const { notifications } = get();
      const notification = notifications.find(n => n.id === notificationId);

      if (notification?.type === 'lab') {
        // Call API to delete the notification from the database
        const result = await API.dismissNotification({ notificationId });
        if (result.error) {
          set({ error: 'Failed to dismiss notification' });
          return;
        }
      }

      // Remove from local state
      set((state) => ({
        notifications: state.notifications.filter(n => n.id !== notificationId)
      }));
    } catch (error) {
      console.error('Error dismissing notification:', error);
      set({ error: 'Failed to dismiss notification' });
    }
  },

  loadNotifications: async (customerId: string) => {
    try {
      set({ notificationsLoading: true, error: null });

      // Get lab notifications from API
      const result = await API.getCustomerNotifications({ customerId });
      if (result.error) {
        set({ error: 'Failed to load notifications', notificationsLoading: false });
        return;
      }

      // Transform API notifications to match our interface
      const labNotifications: NotificationItem[] = (result.data || []).map((apiNotification: any) => ({
        id: apiNotification.id?.toString() || Math.random().toString(),
        title: apiNotification.title || 'New Lab Results Available',
        message: apiNotification.message || '',
        type: 'lab' as const,
        timestamp: new Date(apiNotification.created_at || apiNotification.timestamp),
        priority: apiNotification.priority || 'medium',
        dismissable: true,
        // Navigation data for reports page
        binName: apiNotification.binName,
        location: apiNotification.location,
        locationId: apiNotification.locationId,
        cropYear: apiNotification.cropYear
      }));

      set({ notifications: labNotifications, notificationsLoading: false });
    } catch (error) {
      console.error('Error loading notifications:', error);
      set({ error: 'Failed to load notifications', notificationsLoading: false });
    }
  },

  setRecentServices: (storageId: string, services: RecentService[]) => {
    set((state) => ({
      recentServicesByStorage: {
        ...state.recentServicesByStorage,
        [storageId]: services
      },
      servicesLoading: false
    }));
  },

  getRecentServices: (storageId: string) => {
    const { recentServicesByStorage } = get();
    return recentServicesByStorage[storageId] || [];
  },

  setStorageHealthData: (storageId: string, healthData: StorageHealthData) => {
    set((state) => ({
      healthDataByStorage: {
        ...state.healthDataByStorage,
        [storageId]: healthData
      }
    }));
  },

  getStorageHealthData: (storageId: string) => {
    const { healthDataByStorage } = get();
    return healthDataByStorage[storageId] || null;
  },

  setLoading: (loading) => set({ loading }),

  setError: (error) => set({ error }),

  getSelectedStorage: () => {
    const { storages, selectedStorageId } = get();
    return storages.find(s => s.id === selectedStorageId) || null;
  },
}));
